import { all, call, put, select, takeLeading } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { ToastService } from "@launchmetrics/lm-ui";
import { push, routerActions } from "connected-react-router";
import { store } from "store";
import { cloneDeep, isEqual } from "lodash";

import Mekong from "lib/ajax/Mekong";
import {
	resetReport,
	setReportConfiguration,
	setReportError,
	setSaveReportModalOpen,
	setCloseWithoutSavingModalOpen,
	setOriginalDefinition
} from "./reducers";
import { t } from "lib/i18n";
import { buildArticleUrl } from "lib/searchParams";
import { getReportDefinitionSearchParams } from "lib/searchParams";

import { State } from "store/types";
import {
	ReportConfigurationType,
	ReportDefinition,
	ReportDefinitionSettings,
	ReportConfigurationModel,
	FeedClippingSort
} from "store/models/reportConfiguration";
import { operators } from 'store/fletcher/actions';
import { State as FiltersState } from 'store/search/filters/reducers';
import { State as FormState } from 'store/search/form/reducers';
import { State as HorizontalFiltersState } from 'store/search/horizontalFilters/reducers';
import { getPeriodDates } from "lib/period";
import { SearchPeriod } from "types/search/form";
import { addItem as addDownloadItem } from "store/report/reportDownloader";

const WRONG_PARAMS_ERROR = 'reports.side_panel.save_modal.input.error.wrong_params';
const NAME_ALREADY_EXISTS_ERROR ='reports.side_panel.save_modal.input.error.report_name';

export type ReportDefinitionPayload = ReportDefinition;

export type ReportConfigurationPayload = Omit<ReportConfigurationModel, 'id' |'definition'> & {
	id?: string;
	definition: ReportDefinitionPayload;
}

export default function* sagas() {
	yield all([
		takeLeading('report/resetReport', resetReportSaga),
		takeLeading('report/saveReport', saveReport),
		takeLeading('report/setReportById', setReportById),
		takeLeading('report/isSameReportDefinition', isSameReportDefinition),
		takeLeading('report/exportReport', exportReport)
	]);
}

function* resetReportSaga() {
	const search: string = yield select((state: State) => state.router.location.search);
	const queryParams = new URLSearchParams(search);
	queryParams.delete('reportId');
	yield put(routerActions.replace({search: queryParams.toString()}));
}

function* setReportById({ payload: { id, navigate }}: PayloadAction<{ id: number, navigate: boolean }>) {
	const reportConfigurations: ReportConfigurationModel[] = yield select((state: State) => state.report.reportManager.items);
	const reportConfiguration: ReportConfigurationModel | undefined = reportConfigurations.find(report => report.id === id) as ReportConfigurationModel;

	if (!reportConfiguration) {
		yield put(setReportError(`Report ${id} not found`));
		return;
	}

	yield put(setReportConfiguration(reportConfiguration));
	yield put(setOriginalDefinition(reportConfiguration.definition));

	if (navigate) {
		const reportSearch = cloneDeep(reportConfiguration.definition.search);

		if (reportSearch.period !== 'custom') {
			const { beginDate, endDate } = getPeriodDates(reportSearch.period as SearchPeriod);
			reportSearch.begin_date = beginDate?.startOf('day').format('YYYYMMDD');
			reportSearch.end_date = endDate?.endOf('day').format('YYYYMMDD');
		}

		let url = buildArticleUrl(reportSearch);
		url = url.replace(process.env.REACT_APP_URL as string, '');
		url += `&reportId=${reportConfiguration.id}`;

		yield put(operators.resetFetchState({ resource: 'urlFilteredMedia' }));
		yield put(routerActions.push(url));
	}
}

function* buildDefinition() {
	const reportType: ReportConfigurationType = yield select((state: State) => state.article.report.configuration.type);
	const definition: ReportDefinition = yield select((state: State) => state.article.report.configuration.definition);
	const searchFilters: FiltersState = yield select((state: State) => state.search.filters);
	const searchForm: FormState = yield select((state: State) => state.search.form);
	const horizontalFilters: HorizontalFiltersState = yield select((state: State) => state.search.horizontalFilters);

	const search = getReportDefinitionSearchParams(searchForm, searchFilters, horizontalFilters);

	const sortBy: FeedClippingSort = yield select((state: State) => state.search.form.sort);
	const feedClippings = definition.settings.feedClippings.map(feedClipping =>
		({...feedClipping, sort: sortBy })
	);
	const definitionType = getDefinitionReportType(reportType, definition.settings);

	return {
		...definition,
		search,
		settings: {
			...definition.settings,
			type: definitionType,
			feedClippings
		}
	};
}

function* exportReport(params: {type: string, payload?: ReportConfigurationModel}) {
	let reportConfiguration: ReportConfigurationModel;

	if (params.payload) reportConfiguration = params.payload;
	else {
		reportConfiguration = {
			...yield select((state: State) => state.article.report.configuration),
			name: 'Untitled',
			definition: yield call(buildDefinition)
		};
	}

	try {
		const {id, status }: {id: number, status: string} = yield call([Mekong, 'post'], `/v1/report/export`, { data: reportConfiguration.definition});
		if (status !== 'created') throw new Error('Report export failed');

		yield put(addDownloadItem({
			jobId: id,
			status: 'active',
			progress: 0,
			reportConfiguration
		}));

		ToastService.show({
			id: 'report-export-error',
			title: t('reports.action.export.success.title'),
			description: t('reports.action.export.success.description'),
			type: 'success'
		});

	} catch(err) {
		console.error(err);
		ToastService.show({
			id: 'report-export-error',
			title: t('reports.action.export.error.title'),
			description: t('reports.action.export.error.description'),
			type: 'error'
		});
	}
}

function* isSameReportDefinition() {
	const originDefinition: ReportDefinition = cloneDeep((yield select((state: State) => state.article.report.originalReportDefinition)) as ReportDefinition);
	const currentDefinition: ReportDefinition = yield call(buildDefinition);

	// We stringify the objects to compare them without undefined properties that cause isEqual to be false
	if (isEqual(JSON.stringify(originDefinition), JSON.stringify(currentDefinition))) {
		yield put(resetReport());
	}
	else {
		yield put(setCloseWithoutSavingModalOpen(true));
	}
}

function* saveReport() {
	const name: string = yield select((state: State) => state.article.report.configuration.name);
	if (!name.trim()) {
		yield put(setReportError(WRONG_PARAMS_ERROR));
		return;
	}

	const reportType: ReportConfigurationType = yield select((state: State) => state.article.report.configuration.type);
	const definition: ReportDefinitionPayload = yield call(buildDefinition);

	const reportConfigurationPayload: ReportConfigurationPayload = {
		name,
		type: reportType,
		definition: definition
	};

	try {
		yield call([Mekong, 'post'], `/v1/report/configuration`, { data: reportConfigurationPayload});


		yield put(operators.resetFetchState({ resource: 'reportConfigurations' }));
		yield put(setReportError(''));
		yield put(setSaveReportModalOpen(false));
		yield put(setCloseWithoutSavingModalOpen(false));
		yield put(resetReport());

		ToastService.show({
			id: 'report-saved-toast',
			title: t('reports.saved_success_title'),
			description: t('reports.saved_success_description'),
			type: 'success',
			action: {
				text: t('reports.saved_success_action'),
				fn: () => {
					store.dispatch(push('/report'));
					ToastService.remove('report-saved-toast');
				}
			}
		});
	} catch (error: unknown) {
		const code = (error as any).code;
		switch (code) {
			case 'WRONG_PARAMETER':
				yield put(setReportError(WRONG_PARAMS_ERROR));
				break;
			case 'NAME_ALREADY_EXISTS':
				yield put(setReportError(NAME_ALREADY_EXISTS_ERROR));
				break;
		}
	}
}

function getDefinitionReportType(reportConfigurationType: ReportConfigurationType, reportSettings: ReportDefinitionSettings){
	if (reportConfigurationType === 'online') return 'coverage';
	if (reportSettings.minDate && reportSettings.maxDate) return 'periodic';
	return 'campaign';
}
